import { IconButton, Menu, MenuItem } from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { OrganizeService } from '@affine/core/modules/organize';
import {
  FolderTemplateService,
  TemplateDocService,
} from '@affine/core/modules/template-doc';
import { useI18n } from '@affine/i18n';
import { TemplateIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback } from 'react';

const TemplateFolderItem = ({
  folderId,
  onSelect,
}: {
  folderId: string;
  onSelect: (folderId: string) => void;
}) => {
  const organizeService = useService(OrganizeService);
  const node = useLiveData(organizeService.folderTree.folderNode$(folderId));
  const name = useLiveData(node?.name$);

  const handleClick = useCallback(() => {
    onSelect(folderId);
  }, [folderId, onSelect]);

  if (!node) {
    return null;
  }

  return (
    <MenuItem
      prefixIcon={<TemplateIcon />}
      onClick={handleClick}
      data-testid={`folder-template-item-${folderId}`}
    >
      {name || 'Untitled'}
    </MenuItem>
  );
};

export const NewFromFolderTemplateButton = () => {
  const t = useI18n();
  const templateDocService = useService(TemplateDocService);
  const folderTemplateService = useService(FolderTemplateService);
  const templateFolderIds = useLiveData(
    templateDocService.folderList.templateFolderIds$
  );

  const handleSelect = useAsyncCallback(
    async (folderId: string) => {
      await folderTemplateService.instantiate(folderId, null);
    },
    [folderTemplateService]
  );

  if (templateFolderIds.length === 0) {
    return null;
  }

  return (
    <Menu
      items={templateFolderIds.map(id => (
        <TemplateFolderItem key={id} folderId={id} onSelect={handleSelect} />
      ))}
    >
      <IconButton
        data-testid="navigation-panel-bar-new-from-folder-template-button"
        size="16"
        tooltip={t['com.affine.folder-template.new-from-template']()}
      >
        <TemplateIcon />
      </IconButton>
    </Menu>
  );
};
